import { createContext, useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API_URL = `${BACKEND_URL}/api`;
const API_TIMEOUT_MS = 300000;
const AUTH_INIT_TIMEOUT_MS = 8000; // loading ekranı max 8 saniye

axios.defaults.timeout = API_TIMEOUT_MS;

// Instagram in-app browser ve bazı tarayıcılarda localStorage erişimi
// SecurityError fırlatabilir — tüm erişimleri bu wrapper üzerinden yapıyoruz.
const safeStorage = {
  getItem: (key) => {
    try { return localStorage.getItem(key); } catch { return null; }
  },
  setItem: (key, value) => {
    try { localStorage.setItem(key, value); } catch {}
  },
  removeItem: (key) => {
    try { localStorage.removeItem(key); } catch {}
  },
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => safeStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const logoutTimerRef = useRef(null);

  const clearAuth = (message, type = 'info') => {
    if (message) {
      if (type === 'error') toast.error(message);
      else if (type === 'success') toast.success(message);
      else toast.info(message);
    }
    safeStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const parseJwt = (jwt) => {
    try {
      const base64Url = jwt.split('.')[1];
      if (!base64Url) return null;
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch {
      return null;
    }
  };

  const scheduleAutoLogout = (jwt) => {
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }
    const payload = parseJwt(jwt);
    const exp = payload?.exp;
    if (!exp) return;
    const msUntilExpiry = exp * 1000 - Date.now();
    if (msUntilExpiry <= 0) {
      clearAuth('Oturum süreniz doldu. Lütfen tekrar giriş yapın.');
      return;
    }
    // add small buffer to avoid edge timing issues
    logoutTimerRef.current = setTimeout(() => {
      clearAuth('Oturum süreniz doldu. Lütfen tekrar giriş yapın.');
    }, msUntilExpiry + 1000);
  };

  useEffect(() => {
    // Global interceptor: if token is expired/invalidated, log out everywhere in the app.
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = error?.response?.status;
        const detail = error?.response?.data?.detail;
        const url = error?.config?.url || '';
        const isAuthCall =
          url.includes('/auth/login') ||
          url.includes('/auth/register') ||
          url.includes('/auth/logout');

        if (!isAuthCall && (status === 401 || status === 403) && safeStorage.getItem('token')) {
          if (detail === 'Session invalidated by another login') {
            clearAuth('Bu hesap başka bir cihazda giriş yaptı. Oturumunuz kapatıldı.', 'error');
          } else if (detail === 'Token expired' || detail === 'Please login again') {
            clearAuth('Oturum süreniz doldu. Lütfen tekrar giriş yapın.');
          } else if (detail === 'Account pending admin approval') {
            clearAuth('Hesabınız admin onayı bekliyor.');
          } else {
            // Default: any unauthorized/forbidden response should end the session
            clearAuth('Oturumunuz geçersiz. Lütfen tekrar giriş yapın.');
          }
        }

        return Promise.reject(error);
      }
    );

    const verifySession = async () => {
      const storedToken = safeStorage.getItem('token');
      if (!storedToken) return;
      try {
        await axios.get(`${API_URL}/auth/me`, {
          headers: { Authorization: `Bearer ${storedToken}` }
        });
      } catch (e) {
        // Let interceptor handle clearing/toast, but also ensure local state is cleared
        const detail = e?.response?.data?.detail;
        if (detail === 'Session invalidated by another login') {
          clearAuth('Bu hesap başka bir cihazda giriş yaptı. Oturumunuz kapatıldı.', 'error');
        } else if (detail === 'Token expired' || detail === 'Please login again') {
          clearAuth('Oturum süreniz doldu. Lütfen tekrar giriş yapın.');
        } else if (detail === 'Account pending admin approval') {
          clearAuth('Hesabınız admin onayı bekliyor.');
        } else {
          clearAuth('Oturumunuz geçersiz. Lütfen tekrar giriş yapın.');
        }
      }
    };

    const initAuth = async () => {
      // Güvenlik ağı: herhangi bir nedenle takılırsa 8 sn sonra loading'i kapat
      const safetyTimer = setTimeout(() => setLoading(false), AUTH_INIT_TIMEOUT_MS);
      try {
        const storedToken = safeStorage.getItem('token');
        if (storedToken) {
          const payload = parseJwt(storedToken);
          if (payload?.exp && payload.exp * 1000 <= Date.now()) {
            clearAuth('Oturum süreniz doldu. Lütfen tekrar giriş yapın.');
            return;
          }
          try {
            const response = await axios.get(`${API_URL}/auth/me`, {
              headers: { Authorization: `Bearer ${storedToken}` },
              timeout: 7000,
            });
            setUser(response.data);
            setToken(storedToken);
            scheduleAutoLogout(storedToken);
          } catch (error) {
            const detail = error?.response?.data?.detail;
            if (detail === 'Session invalidated by another login') {
              clearAuth('Bu hesap başka bir cihazda giriş yaptı. Oturumunuz kapatıldı.', 'error');
            } else if (detail === 'Token expired' || detail === 'Please login again') {
              clearAuth('Oturum süreniz doldu. Lütfen tekrar giriş yapın.');
            } else if (detail === 'Account pending admin approval') {
              clearAuth('Hesabınız admin onayı bekliyor.');
            } else {
              clearAuth(null);
            }
          }
        }
      } catch {
        // localStorage veya diğer API hatası — oturumu sıfırla, login'e yönlendir
        clearAuth(null);
      } finally {
        clearTimeout(safetyTimer);
        setLoading(false);
      }
    };
    initAuth();

    // When user comes back to the tab, verify session (kicks out if logged in elsewhere)
    const onFocus = () => verifySession();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') verifySession();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      axios.interceptors.response.eject(interceptorId);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
      if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    };
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    safeStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    scheduleAutoLogout(access_token);
    return userData;
  };

  const register = async (email, password, fullName) => {
    const response = await axios.post(`${API_URL}/auth/register`, {
      email,
      password,
      full_name: fullName
    });
    const data = response.data;
    
    // If backend returned a token, user is approved (or admin)
    if (data?.access_token && data?.user) {
      safeStorage.setItem('token', data.access_token);
      setToken(data.access_token);
      setUser(data.user);
      scheduleAutoLogout(data.access_token);
      return data.user;
    }
    
    // Otherwise: pending approval
    return data;
  };

  const logout = async () => {
    try {
      if (token) {
        await axios.post(`${API_URL}/auth/logout`, {}, { headers: { Authorization: `Bearer ${token}` } });
      }
    } catch (_) {
      // ignore network errors on logout
    }
    clearAuth(null);
  };

  const getAuthHeader = () => ({
    Authorization: `Bearer ${token}`
  });

  const acceptDisclaimer = async () => {
    if (!token) {
      throw new Error('No active session');
    }

    const response = await axios.post(
      `${API_URL}/auth/disclaimer/accept`,
      {},
      { headers: { Authorization: `Bearer ${token}` } }
    );

    setUser((prevUser) => {
      if (!prevUser) return prevUser;
      return {
        ...prevUser,
        disclaimer_accepted: true,
        disclaimer_accepted_at: response.data?.disclaimer_accepted_at || new Date().toISOString(),
      };
    });

    return response.data;
  };

  const refreshUser = async () => {
    if (!token) return null;
    const response = await axios.get(`${API_URL}/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    setUser(response.data);
    return response.data;
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, getAuthHeader, acceptDisclaimer, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
